/** Workflows P5-5: the Data connector step (`http_request`). Merged into `engage.workflows` (steps, summaries, exits, incomplete) and kept whole at `engage.workflows.connector`. */
export const workflowsConnectorStep = {
  steps: { http_request: "Data connector" },
  summaries: { http_request: "{{method}} {{path}} · {{endpoint}}", http_request_operation: "Call {{operation}}" },
  exits: { requestSucceeded: "Request succeeded", requestFailed: "Request failed", requestNotFound: "Nothing found", requestRejected: "The system refused", requestUnavailable: "System unavailable" },
  conditionGroups: { variable: "Variables", connector: "Connected system" },
  incomplete: { endpoint: "Choose the endpoint to call", httpBody: "Fix the request body", operation: "Choose the connector operation to call" },
  connector: {
    mode: {
      label: "How the step calls the system",
      operation: "Connector operation",
      inline: "Inline request",
      operationHint: "Pick one of the operations you set up in Settings → Connectors. It already knows its address, sign-in and answer.",
      inlineHint: "The original data connector: a request to a webhook endpoint, with the path, body and answer mapped here.",
    },
    op: {
      plan: "Calling a connector operation needs the {{plan}} plan or higher.",
      runsBehind: "Runs behind the scenes. The connector's own timeout and retries apply; the workflow carries on after it.",
      runsFacing: "The visitor waits while this runs: one attempt, up to {{seconds}} seconds. A call that takes longer counts as failed.",
      connector: "Connector",
      connectorHint: "The connection to the system you want to call.",
      chooseConnector: "Choose a connector",
      connectorGone: "This connector no longer exists. Choose another.",
      connectorGoneOption: "Unavailable connector",
      connectorInactive: "This connector is not active, so the step would fail. Activate it in Settings → Connectors.",
      inactiveOption: "{{name}} ({{status}})",
      sandboxOption: "{{name}} (sandbox)",
      statuses: { draft: "draft", paused: "paused", error: "needs attention" },
      operation: "Operation",
      operationHint: "What to ask the system, such as “Look up a student”.",
      chooseOperation: "Choose an operation",
      operationGone: "This connector has no operation with that key any more. Choose another.",
      operationGoneOption: "Unavailable operation ({{key}})",
      operationUnpublished: "This operation has no published version yet, so the step would fail. Publish it in Settings → Connectors.",
      unpublishedOption: "{{name}} (not published)",
      version: "Version {{version}}",
      kinds: { read: "Reads", write: "Changes data", destructive: "Deletes data" },
      writeNote: "This operation changes data in the other system. It runs every time the workflow reaches this step.",
      loading: "Loading connectors",
      loadingOperations: "Loading operations",
      loadingSignature: "Loading the operation's details",
      loadFailedTitle: "We couldn't load the connectors",
      operationsFailedTitle: "We couldn't load the operations",
      loadFailedBody: "Check your connection and try again.",
      signatureFailed: "We couldn't read this operation's parameters and outputs. Close the step and open it again.",
      retry: "Try again",
      noAccess: "You need permission to view connectors to choose one. Ask an admin to do it.",
      emptyTitle: "No connector yet",
      emptyBody: "Connect a system in Settings → Connectors, add an operation and publish it. Then a workflow can call it.",
      add: "Add a connector",
      openConnectors: "Open Connectors",
      noOperationsTitle: "This connector has no operations",
      noOperationsBody: "Add an operation to it and publish it, then come back.",
      facts: "After the call, later steps can read",
      params: {
        title: "Parameters",
        hint: "Fill each parameter from the run. A value can use variables, such as {{example}}.",
        none: "This operation takes no parameters.",
        requiredLabel: "{{name}} (required)",
        required: "This parameter is required.",
        tooLong: "The value is too long (at most {{max}} characters).",
        oneOf: "One of: {{values}}.",
        types: { string: "Text", integer: "Whole number", number: "Number", boolean: "Yes or no (true / false)", array: "A list", object: "An object" },
        stale: "The parameter {{name}} is not part of this operation any more.",
        removeStale: "Remove",
      },
      outputs: {
        title: "Outputs",
        hint: "Values the operation gives back. Each is saved in a run variable such as {{example}} for the steps after it.",
        none: "This operation gives no outputs.",
        into: "Save in",
        intoFor: "Run variable for {{name}}",
        problems: { variable: "Use var. and then letters, numbers or _ (for example var.status).", duplicate: "Another output already fills this variable." },
      },
      test: {
        title: "Test",
        intro: "Calls the operation once through the Connectors test console, with sample values. Nothing else in the workflow runs.",
        sample: "Sample values",
        sampleHint: "The variables your parameters use. Type what a run would have.",
        samplePlaceholder: "A sample value",
        run: "Test the call",
        running: "Calling…",
        locked: "Testing needs the {{plan}} plan or higher.",
        needOperation: "Choose a connector and an operation first.",
        noAccess: "You need permission to use the test console to test.",
        succeeded: "The call worked",
        failed: "The call did not work",
        status: "Status {{status}}",
        noStatus: "No answer",
        duration: "{{ms}} ms",
        environment: "Environment: {{environment}}",
        valuesTitle: "Values it would save",
        valuesNone: "The operation returned nothing to save.",
        refused: { invalid_params: "A parameter is missing or has the wrong type.", connector_inactive: "The connector is not active.", not_published: "The operation has no published version.", unknown: "The call was refused before it left." },
        callFailed: {
          rate_limited: "Too many tests. Wait a minute and try again.",
          not_found: "The connector or operation was not found.",
          forbidden: "You don't have permission to test operations.",
          plan: "Your plan doesn't include the Connectors test console.",
          network: "We couldn't reach Sagot. Check your connection and try again.",
          unknown: "The test could not be run. Try again.",
        },
      },
    },
    plan: "The data connector needs the {{plan}} plan or higher.",
    planBadge: "{{plan}} plan",
    runsBehind: "Runs behind the scenes. Up to {{seconds}} seconds for the answer; the workflow carries on after it.",
    runsFacing: "The visitor waits while this request runs: up to {{seconds}} seconds. A request that takes longer counts as failed.",
    fields: {
      method: "Method",
      path: "Path",
      pathHint: "Added to the endpoint's address, such as {{example}}. Variables are encoded for you.",
      timeout: "Wait for the answer up to (seconds)",
      timeoutHint: "1 to {{max}} seconds.",
      timeoutHintFacing: "A workflow the visitor sees waits for the answer, so this is at most {{max}} seconds.",
      body: "Body",
      bodyHint: "A JSON object. Put every variable inside quotes, as in {{example}}.",
      bodyNone: "A GET request has no body.",
      bodyOnGet: "This request still has a body, and a GET request can't have one. Clear it, or choose POST, PUT or PATCH.",
      clearBody: "Clear the body",
      variables: "Variables you can use",
      variablesEarlier: "and the values saved by earlier steps, such as {{example}}",
    },
    problems: {
      path: { long: "The path is too long (at most {{max}} characters).", slash: "Start the path with a slash, as in /students.", chars: "The path can't contain ?, #, @, a backslash or a space. Use the parameters below for the query.", dots: "The path can't contain a . or .. part." },
      body: { json: "This isn't valid JSON yet. Put every variable inside quotes.", object: "Use a JSON object, like the example in the box." },
      row: { name: "Use letters, numbers and - only (queries also allow . and _).", forbidden: "Credentials can't go in the workflow. Set them on the endpoint in Settings → Webhooks.", duplicate: "This name is used twice.", value: "The value is too long." },
      map: { pointer: "Write a field path that starts with a slash, like /data/status.", variable: "Use letters, numbers and _ for the variable name.", duplicate: "Another row already fills this variable." },
    },
    endpoint: {
      label: "Endpoint",
      hint: "A webhook endpoint set to “Only from my workflows” with the Sagot format. It holds the address and the signing secret.",
      choose: "Choose an endpoint",
      disabledOption: "{{name}} (disabled)",
      unavailableOption: "Unavailable endpoint",
      unavailable: "This endpoint is gone, disabled or no longer set to “Only from my workflows”. Choose another or fix it in Settings → Webhooks.",
      loading: "Loading endpoints",
      loadFailedTitle: "We couldn't load the endpoints",
      loadFailedBody: "Check your connection and try again.",
      retry: "Try again",
      noAccess: "You need permission to manage webhooks to see the endpoints. Ask an admin to choose one.",
      emptyTitle: "No endpoint for workflows yet",
      emptyBody: "Add a webhook endpoint, set it to “Only from my workflows”, and a connector can call it.",
      add: "Add endpoint",
      openWebhooks: "Open Webhooks",
      authSet: "Sends the {{header}} header (••••{{last4}}) with every request.",
      authNone: "No authentication is set on this endpoint. If the API needs a key, add it in Settings → Webhooks.",
    },
    rows: {
      name: "Name",
      value: "Value",
      remove: "Remove",
      query: { label: "Query parameters", add: "Add parameter", empty: "None", namePlaceholder: "grade", valuePlaceholder: "11" },
      headers: { label: "Headers", hint: "For labels such as X-Branch. Passwords and keys go on the endpoint, not here.", add: "Add header", empty: "None", namePlaceholder: "X-Branch", valuePlaceholder: "Main" },
    },
    map: {
      label: "Save the answer",
      hint: "Pick fields of the JSON answer and give each a variable. Later steps use them as {{example}}. Text, numbers and true/false can be saved; lists and objects can't.",
      pointer: "Field in the answer",
      pointerPlaceholder: "/data/status",
      variable: "Variable",
      variablePlaceholder: "enrol_status",
      add: "Add field",
      remove: "Remove field",
      empty: "Nothing is saved from the answer. The steps after this one still know if it worked: {{ok}} and {{status}}.",
      row: "Field {{n}}",
      facts: "Also available afterwards",
    },
    exits: {
      label: "After the request",
      hint: "Unset exits carry on with the next step. A failed request is written to the run log.",
      success: "If it succeeded, go to",
      failure: "If it failed, go to",
      next: "The next step",
    },
    test: {
      title: "Test request",
      intro: "Sends the request once, with the values below. You see what came back, not the answer itself.",
      needEndpoint: "Choose an endpoint to test.",
      needFix: "Fix the request first.",
      locked: "Testing needs the {{plan}} plan.",
      sample: "Values for the test",
      sampleHint: "Used in place of the variables while testing.",
      samplePlaceholder: "A sample value",
      run: "Test request",
      running: "Testing…",
      resultTitle: "Result",
      status: "Status {{status}}",
      noStatus: "No answer",
      duration: "{{ms}} ms",
      succeeded: "The request worked",
      failed: "The request failed",
      mappedTitle: "What would be saved",
      mappedNone: "Nothing is mapped yet. Pick fields below.",
      skippedTitle: "Not saved",
      skipped: { missing: "{{to}}: the field isn't in the answer", not_scalar: "{{to}}: the field is a list or an object" },
      fieldsTitle: "Fields in the answer",
      fieldsNone: "The answer has no fields to save.",
      useField: "Use as a variable",
      useFieldFor: "Use {{pointer}} as a variable",
      mapped: "Saved",
      notMappable: "Lists, objects and null can't be saved",
      callFailed: {
        rate_limited: "You've tested a lot just now. Wait a minute and try again.",
        not_found: "That endpoint no longer exists. Choose another.",
        forbidden: "You don't have permission to test requests.",
        plan: "Testing needs the Pro plan.",
        network: "We couldn't reach the server. Check your connection and try again.",
        unknown: "The test didn't run. Try again.",
      },
    },
    errors: {
      endpoint_unavailable: "The endpoint is disabled, deleted or not set to “Only from my workflows”.",
      blocked_destination: "The address isn't allowed. Only public https addresses can be called.",
      bad_request: "The request couldn't be built. Check the path, the parameters and the body.",
      timeout: "The other system took too long to answer.",
      transport: "We couldn't connect to the other system.",
      http_status: "The other system answered with an error status.",
      too_large: "The answer is too large.",
      invalid_json: "The answer isn't JSON.",
      rate_limited: "Too many requests were sent just now. Try again in a moment.",
      run_cap: "This run has already sent the most requests it is allowed.",
      time_budget: "The visitor has already waited the most this conversation turn allows.",
      plan: "Your plan doesn't include the data connector.",
      unknown: "The request failed.",
    },
  },
} as const;
